import { useMemo, useState } from "react";
import { useFormikContext } from "formik";

import { Role } from "@typedefs/interfaces";
import { useGetProjectsQuery } from "@app/features/project/projectApiSlice";
import {
	useGetMyNotificationPreferencesQuery,
	useSetNotificationPreferenceMutation,
} from "@app/features/project/notificationPreferencesApiSlice";
import TranslatableText from "@components/translations/TranslatableText";
import NotificationSwitch from "@components/input/NotificationSwitch";

// Only project admins may subscribe — the API answers 403 for anyone else, so the
// list shows the projects where the toggle can actually do something.
// ponytail: one page of 100 covers every realistic account; paginate if that stops being true.
const PROJECTS_QUERY = {
	url: "projects/?page_size=100",
	fast: true,
	showFinalized: false,
	showTrashbin: false,
};

const ProjectNotificationsSection = () => {
	const { values } = useFormikContext<{ isAgreeToEmailUpdates: boolean }>();
	const [failedProjectId, setFailedProjectId] = useState<number | null>(null);
	const [pendingProjectId, setPendingProjectId] = useState<number | null>(null);

	const { data: projects, isLoading: isLoadingProjects } = useGetProjectsQuery(PROJECTS_QUERY);
	const { data: preferences, isLoading: isLoadingPreferences } =
		useGetMyNotificationPreferencesQuery();
	const [setPreference] = useSetNotificationPreferenceMutation();

	const adminProjects = useMemo(
		() => (projects?.results ?? []).filter((project) => project.role?.includes(Role.ADMIN)),
		[projects],
	);

	const subscribedProjectIds = useMemo(
		() =>
			new Set(
				(preferences ?? [])
					.filter((preference) => preference.is_subscribed)
					.map((preference) => preference.project.id),
			),
		[preferences],
	);

	const handleToggle = async (projectId: number, isSubscribed: boolean) => {
		setFailedProjectId(null);
		setPendingProjectId(projectId);
		try {
			await setPreference({ project: projectId, is_subscribed: !isSubscribed }).unwrap();
		} catch {
			setFailedProjectId(projectId);
		} finally {
			setPendingProjectId(null);
		}
	};

	const isLoading = isLoadingProjects || isLoadingPreferences;

	return (
		<section className="pe-3 d-flex f-column gap-8">
			<h3 className="pt-2">
				<TranslatableText translationKey="user.project_notifications" />
			</h3>

			<p className="fs-12 project-notifications-intro">
				<TranslatableText translationKey="user.project_notifications_intro" />
			</p>

			{!values.isAgreeToEmailUpdates && (
				<p className="fs-12 project-notifications-notice">
					<TranslatableText translationKey="user.project_notifications_paused" />
				</p>
			)}

			{isLoading && (
				<p className="fs-12 project-notifications-intro">
					<TranslatableText translationKey="main.loading" />
				</p>
			)}

			{!isLoading && adminProjects.length === 0 && (
				<p className="fs-12 project-notifications-intro">
					<TranslatableText translationKey="user.project_notifications_empty" />
				</p>
			)}

			{!isLoading && adminProjects.length > 0 && (
				<ul className="project-notifications-list">
					{adminProjects.map((project) => {
						const isSubscribed = subscribedProjectIds.has(project.id);
						return (
							<li key={project.id} className="project-notifications-row">
								<div className="d-flex f-column">
									<span className="fs-12">{project.name}</span>
									{failedProjectId === project.id && (
										<span className="fs-9 project-notifications-error">
											<TranslatableText translationKey="user.project_notifications_failed" />
										</span>
									)}
								</div>
								<NotificationSwitch
									isOn={isSubscribed}
									isPending={pendingProjectId === project.id}
									label={project.name}
									onToggle={() => handleToggle(project.id, isSubscribed)}
								/>
							</li>
						);
					})}
				</ul>
			)}
		</section>
	);
};

export default ProjectNotificationsSection;
